/**
 * `EntitlementOverridesCard` — per-account quota overrides.
 *
 * Each of the four dimensions can be overridden with a custom limit
 * (blank = unlimited, omitted = "use the plan's limit"). The wire
 * shape is PUT-replace: every save sends the COMPLETE override set,
 * and the server replaces what's there.
 *
 * INERT (Phase A): overrides recorded here are catalog metadata
 * only. They take effect when the enforcement runtime (Phase C)
 * ships. The InertBanner on the parent (SubscriptionCard) and the
 * inline note below make that explicit.
 */

import { useEffect, useState } from 'react';

import type {
  AdminEntitlementOverride,
  AdminEntitlementOverridesPutBody,
  AdminPlanDimension,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { useC } from '../../lib/theme.js';
import {
  ALL_DIMENSIONS,
  dimensionLabel,
} from '../plans/plan-helpers.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

interface OverrideRow {
  /** Empty string = unlimited (when enabled). */
  readonly limit: string;
  /** True when this dimension has an override. False = no override
   *  (omitted from the PUT). */
  readonly enabled: boolean;
}

type FormState = Record<AdminPlanDimension, OverrideRow>;

function blankForm(): FormState {
  const out = {} as FormState;
  for (const d of ALL_DIMENSIONS) {
    out[d] = { limit: '', enabled: false };
  }
  return out;
}

function overridesToForm(
  overrides: readonly AdminEntitlementOverride[],
): FormState {
  const form = blankForm();
  for (const o of overrides) {
    form[o.dimension] = {
      limit: o.limit_value === null ? '' : String(o.limit_value),
      enabled: true,
    };
  }
  return form;
}

export interface EntitlementOverridesCardProps {
  accountId: string;
  overrides: readonly AdminEntitlementOverride[];
  onUpdated: (next: readonly AdminEntitlementOverride[]) => void;
}

export function EntitlementOverridesCard({
  accountId,
  overrides,
  onUpdated,
}: EntitlementOverridesCardProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();

  const [form, setForm] = useState<FormState>(() => overridesToForm(overrides));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successAt, setSuccessAt] = useState<number | null>(null);

  useEffect(() => {
    setForm(overridesToForm(overrides));
  }, [overrides]);

  const updateRow = (
    d: AdminPlanDimension,
    patch: Partial<OverrideRow>,
  ): void => {
    setForm((cur) => ({ ...cur, [d]: { ...cur[d], ...patch } }));
  };

  const validate = (): string | null => {
    for (const d of ALL_DIMENSIONS) {
      const row = form[d];
      if (!row.enabled) continue;
      const trimmed = row.limit.trim();
      if (trimmed.length === 0) continue; // blank = unlimited
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
        return `${dimensionLabel(d)} override must be a non-negative whole number, or blank for unlimited.`;
      }
    }
    return null;
  };

  const doSave = async (): Promise<void> => {
    if (saving) return;
    const v = validate();
    if (v !== null) {
      setError(v);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const wire: AdminEntitlementOverridesPutBody = {
        overrides: ALL_DIMENSIONS.filter((d) => form[d].enabled).map((d) => {
          const limit = form[d].limit.trim();
          return {
            dimension: d,
            limit_value: limit.length === 0 ? null : Number(limit),
          };
        }),
      };
      const res = await api.putEntitlementOverrides(accountId, wire);
      onUpdated(res.overrides);
      setSuccessAt(Date.now());
    } catch (err) {
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? `: ${err.server_reason}`
          : '';
      setError(`Could not save overrides${reason}.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card style={{ padding: 20 }}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >
          Entitlement overrides
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.dim,
            letterSpacing: 1,
          }}
        >
          Per-account · applies once enforcement ships
        </div>
      </div>

      <div
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          lineHeight: 1.5,
          marginBottom: 12,
        }}
      >
        Per-dimension override on top of the assigned plan&rsquo;s limits.
        Enable a row to set a custom limit (blank = unlimited). Saving
        REPLACES the override set — disabled dimensions fall back to the
        plan&rsquo;s value.
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void doSave();
        }}
        className="grid grid-cols-1 gap-2"
      >
        {ALL_DIMENSIONS.map((d) => (
          <OverrideRowEditor
            key={d}
            dimension={d}
            row={form[d]}
            onChange={(patch) => updateRow(d, patch)}
          />
        ))}

        <div className="flex items-center justify-end gap-3 pt-2 flex-wrap">
          {error !== null ? (
            <span
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.coralFg,
              }}
            >
              {error}
            </span>
          ) : successAt !== null && Date.now() - successAt < 4000 ? (
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.limeFg,
                letterSpacing: 2,
                textTransform: 'uppercase',
              }}
            >
              Overrides saved
            </span>
          ) : null}
          <button
            type="submit"
            disabled={saving}
            style={{
              background: C.lime,
              color: C.ink,
              fontFamily: sansFont,
              fontSize: 13,
              fontWeight: 600,
              padding: '10px 16px',
              borderRadius: 2,
              border: 0,
              opacity: saving ? 0.6 : 1,
              minHeight: 44,
            }}
          >
            {saving ? 'Saving…' : 'Save overrides'}
          </button>
        </div>
      </form>
    </Card>
  );
}

function OverrideRowEditor({
  dimension,
  row,
  onChange,
}: {
  dimension: AdminPlanDimension;
  row: OverrideRow;
  onChange: (patch: Partial<OverrideRow>) => void;
}): JSX.Element {
  const C = useC();
  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-3 py-2"
      style={{
        background: C.surface2,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
      }}
    >
      <label
        className="flex items-center gap-2 sm:w-48 flex-shrink-0"
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.text,
          cursor: 'pointer',
          fontWeight: 500,
        }}
      >
        <input
          type="checkbox"
          checked={row.enabled}
          onChange={(e) => onChange({ enabled: e.target.checked })}
          style={{ accentColor: C.limeFg, flexShrink: 0 }}
        />
        {dimensionLabel(dimension)}
      </label>
      <input
        type="text"
        inputMode="numeric"
        value={row.limit}
        onChange={(e) => onChange({ limit: e.target.value })}
        disabled={!row.enabled}
        placeholder={row.enabled ? 'Unlimited' : 'Use plan limit'}
        className="flex-1 px-3 py-2 outline-none"
        style={{
          fontFamily: monoFont,
          fontSize: 13,
          color: row.enabled ? C.text : C.dim,
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          opacity: row.enabled ? 1 : 0.6,
        }}
        aria-label={`${dimensionLabel(dimension)} override`}
      />
    </div>
  );
}
